import type { ReactNode } from "react";
import type { IconSvgElement } from "@hugeicons/react";
import { HugeiconsIcon } from "@hugeicons/react";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Button, ButtonVariant } from "shared/components/Button";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components/IconButton";
import { Modal } from "shared/components";
import { Show } from "shared/components/helpers/Show";
import { cx } from "shared/utils";

export type ConfirmDialogVariant =
  | "danger"
  | "primary"
  | "warning"
  | "success"
  | "base";

const iconVariantStyles: Record<
  ConfirmDialogVariant,
  { bg: string; color: string }
> = {
  danger: { bg: "bg-danger-50", color: "text-danger-500" },
  primary: { bg: "bg-primary-50", color: "text-primary-500" },
  warning: { bg: "bg-warning-50", color: "text-warning-500" },
  success: { bg: "bg-success-50", color: "text-success-500" },
  base: { bg: "bg-alpha-40", color: "text-content-800" }
};

const confirmVariantMap: Record<ConfirmDialogVariant, ButtonVariant> = {
  danger: ButtonVariant.DANGER,
  primary: ButtonVariant.PRIMARY,
  warning: ButtonVariant.PRIMARY,
  success: ButtonVariant.PRIMARY,
  base: ButtonVariant.PRIMARY
};

interface ConfirmDialogIcon {
  icon: IconSvgElement;
  variant?: ConfirmDialogVariant;
}

interface ConfirmDialogButton {
  label: string;
  loading?: boolean;
  disabled?: boolean;
}

interface ConfirmDialogProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description?: ReactNode;
  variant?: ConfirmDialogVariant;
  icon?: ConfirmDialogIcon;
  confirmButton?: ConfirmDialogButton;
  cancelButton?: ConfirmDialogButton;
  showCloseButton?: boolean;
}

export const ConfirmDialog = ({
  opened,
  onClose,
  onConfirm,
  title,
  description,
  variant = "danger",
  icon,
  confirmButton,
  cancelButton,
  showCloseButton
}: ConfirmDialogProps) => {
  const iconVariant = icon?.variant ?? variant;
  const iconStyles = iconVariantStyles[iconVariant];
  const buttonVariant = confirmVariantMap[variant];

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      panelClassName="max-w-[366px] w-full p-6"
    >
      <Show if={showCloseButton}>
        <div className="flex justify-end">
          <IconButton
            variant={IconButtonVariant.TEXT_BASE}
            HugeIcon={Cancel01Icon}
            onClick={onClose}
            size={IconButtonSize.LG}
          />
        </div>
      </Show>

      {icon ? (
        <div
          className={cx(
            "size-16 flex items-center justify-center rounded-full mb-6 mx-auto",
            iconStyles.bg
          )}
        >
          <HugeiconsIcon
            className={iconStyles.color}
            icon={icon.icon}
            size={32}
          />
        </div>
      ) : null}

      <h1 className="text-xl font-bold text-content-1000 text-center">
        {title}
      </h1>

      <Show if={Boolean(description)}>
        <p className="text-sm font-medium text-content-400 mt-2 text-center">
          {description}
        </p>
      </Show>

      <div className="flex gap-2 mt-6">
        <Button
          className="w-full"
          loading={cancelButton?.loading}
          disabled={cancelButton?.disabled}
          onClick={onClose}
        >
          {cancelButton?.label ?? "Cancel"}
        </Button>
        <Button
          className="w-full"
          variant={buttonVariant}
          loading={confirmButton?.loading}
          disabled={confirmButton?.disabled}
          onClick={onConfirm}
        >
          {confirmButton?.label ?? "Confirm"}
        </Button>
      </div>
    </Modal>
  );
};
